import type { FolderLinkedBriefDto } from '@inference.engine/workspace-api-client/web';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

import { useReadyBriefOptions } from '@/features/manage-project-folders/lib/useReadyBriefOptions';

/**
 * Radix refuses an empty string as an item value, so "no brief" needs a token
 * of its own. A uuid can never collide with it.
 */
// eslint-disable-next-line i18next/no-literal-string -- a Select item value on the wire, never rendered
export const NO_BRIEF = 'none';

interface LinkedBriefFieldProps {
  projectId: string;
  value: string | null;
  onChange: (briefId: string | null) => void;
  disabled?: boolean;
  /** The folder's current brief in edit mode. */
  currentBrief?: FolderLinkedBriefDto | null;
  triggerId?: string;
}

export function LinkedBriefField({
  projectId,
  value,
  onChange,
  disabled,
  currentBrief,
  triggerId,
}: LinkedBriefFieldProps) {
  const { t } = useTranslation('projects');

  const { options, isLoading, isError } = useReadyBriefOptions({ projectId, currentBrief });

  const hasOptions = options.length > 0;

  return (
    <Select
      value={value ?? NO_BRIEF}
      onValueChange={(next) => onChange(next === NO_BRIEF ? null : next)}
      disabled={disabled || isLoading || isError || !hasOptions}
    >
      <SelectTrigger
        id={triggerId}
        className="w-full"
        data-testid="linked-brief-select-trigger"
        aria-label={t('manageProjectFolders.createFolderModal.linkedBrief')}
      >
        {/* The value is rendered rather than left to `placeholder`: "no brief"
            is a real selection here, so the trigger is never empty and a
            placeholder would never show — including the empty-project case,
            which is the one a user most needs explained. */}
        {/* A failed fetch is read before the count, because in edit mode the
            linked brief is re-offered locally: the list is never empty then,
            and "no ready briefs" would be an answer nobody asked the server
            for. */}
        <SelectValue>
          {isError
            ? t('manageProjectFolders.createFolderModal.couldNotLoadTheProjectBriefs')
            : !hasOptions
              ? t('manageProjectFolders.createFolderModal.noReadyBriefsInThisProject')
              : (options.find((brief) => brief.id === value)?.title ??
                t('manageProjectFolders.createFolderModal.noBrief'))}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_BRIEF}>
          {t('manageProjectFolders.createFolderModal.noBrief')}
        </SelectItem>
        {options.map((brief) => (
          <SelectItem key={brief.id} value={brief.id}>
            {brief.title}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
